/**
 * @voltxampere/persistence — Circuits CRUD (Sprint 21)
 *
 * Public API:
 *
 *   putCircuit(circuit, opts?)  — upsert; sets timestamps + byteSize
 *   getCircuit(id)              — single fetch
 *   listCircuits(opts?)         — sorted by updatedAt desc
 *   deleteCircuit(id)           — remove by id
 *   countCircuits()             — row count
 *   storageUsage()              — { bytesUsed, circuitCount }
 *   clearCircuits()             — remove all rows (used by Veri tab)
 *
 * Every function lazily opens the database via `openDb()` so callers
 * can fire-and-forget without managing the connection.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { openDb, requestToPromise, transactionDone } from "./db.js";
import { INDEX_CIRCUITS_UPDATED_AT, STORE_CIRCUITS, } from "./types.js";
/**
 * Insert or update a circuit. Fresh entries get
 * `createdAt = updatedAt = now`; updates preserve the original
 * `createdAt` and bump `updatedAt`.
 */
export async function putCircuit(circuit, opts = {}) {
    const db = await openDb();
    const now = opts.now ?? Date.now();
    const byteSize = opts.byteSize ?? estimateCircuitBytes(circuit);
    const tx = db.transaction(STORE_CIRCUITS, "readwrite");
    const store = tx.objectStore(STORE_CIRCUITS);
    const existing = await requestToPromise(store.get(circuit.id));
    const stored = {
        id: circuit.id,
        schemaVersion: circuit.schemaVersion,
        circuit,
        name: circuit.name,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
        byteSize,
    };
    await requestToPromise(store.put(stored));
    await transactionDone(tx);
    return stored;
}
export async function getCircuit(id) {
    const db = await openDb();
    const tx = db.transaction(STORE_CIRCUITS, "readonly");
    const store = tx.objectStore(STORE_CIRCUITS);
    const result = await requestToPromise(store.get(id));
    await transactionDone(tx);
    return result;
}
export async function listCircuits(opts = {}) {
    const db = await openDb();
    const direction = opts.direction ?? "desc";
    const limit = opts.limit;
    const tx = db.transaction(STORE_CIRCUITS, "readonly");
    const store = tx.objectStore(STORE_CIRCUITS);
    const index = store.index(INDEX_CIRCUITS_UPDATED_AT);
    // The index iterates in ascending order by default; flip the
    // cursor direction for descending.
    const cursorReq = index.openCursor(null, direction === "desc" ? "prev" : "next");
    const out = [];
    await new Promise((resolve, reject) => {
        cursorReq.onsuccess = () => {
            const cursor = cursorReq.result;
            if (!cursor) {
                resolve();
                return;
            }
            out.push(cursor.value);
            if (limit !== undefined && out.length >= limit) {
                resolve();
                return;
            }
            cursor.continue();
        };
        cursorReq.onerror = () => {
            reject(cursorReq.error ?? new Error("listCircuits: cursor failed"));
        };
    });
    await transactionDone(tx);
    return out;
}
export async function deleteCircuit(id) {
    const db = await openDb();
    const tx = db.transaction(STORE_CIRCUITS, "readwrite");
    const store = tx.objectStore(STORE_CIRCUITS);
    await requestToPromise(store.delete(id));
    await transactionDone(tx);
}
export async function countCircuits() {
    const db = await openDb();
    const tx = db.transaction(STORE_CIRCUITS, "readonly");
    const store = tx.objectStore(STORE_CIRCUITS);
    const n = await requestToPromise(store.count());
    await transactionDone(tx);
    return n;
}
export async function storageUsage() {
    // Walk the entire store summing byteSize. For very large databases
    // this is O(n); the AYARLAR "Veri" tab calls it on demand and the
    // expected row count (1-1000) keeps it cheap.
    const db = await openDb();
    const tx = db.transaction(STORE_CIRCUITS, "readonly");
    const store = tx.objectStore(STORE_CIRCUITS);
    const cursorReq = store.openCursor();
    let bytesUsed = 0;
    let circuitCount = 0;
    await new Promise((resolve, reject) => {
        cursorReq.onsuccess = () => {
            const cursor = cursorReq.result;
            if (!cursor) {
                resolve();
                return;
            }
            const row = cursor.value;
            bytesUsed += row.byteSize;
            circuitCount += 1;
            cursor.continue();
        };
        cursorReq.onerror = () => {
            reject(cursorReq.error ?? new Error("storageUsage: cursor failed"));
        };
    });
    await transactionDone(tx);
    return { bytesUsed, circuitCount };
}
export async function clearCircuits() {
    const db = await openDb();
    const tx = db.transaction(STORE_CIRCUITS, "readwrite");
    const store = tx.objectStore(STORE_CIRCUITS);
    await requestToPromise(store.clear());
    await transactionDone(tx);
}
/**
 * Rough byte-size estimate. Uses `JSON.stringify` length as a
 * proxy for the on-disk footprint; IndexedDB's structured-clone
 * encoding is denser, so this overestimates by ~15-25%. Good
 * enough for the "Veri" tab's storage gauge.
 */
export function estimateCircuitBytes(circuit) {
    return JSON.stringify(circuit).length;
}
//# sourceMappingURL=circuits-store.js.map